import { Info } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CustomButton from 'lib/designSystem/Button';
import EnhancedTable from 'lib/designSystem/Table';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney, validatePermissions } from 'lib/functions/utils';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import ExportDialog from '../../../ExportDialog';
import OperationResume from '../../../Simulator/components/OperationResume';
import SimulationTraditional from '../../../Simulator/components/Simulator/components/SimulationTraditional';
import ConfirmingRequestUpdateStatusModal from './components/UpdateStatusModal';

const gridStyle = {
  alignContent: 'center',
  alignItems: 'center',
  padding: '4px',
  paddingRight: 23,
};

export interface UpdateStatusTransitions {
  [key: string]: { key: string; label: string }[];
}

const CustomTooltip = (text: Array<Array<string>>) => (
  <div style={{ padding: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
      <Typography variant="body1">Descripción</Typography>
    </div>
    <Table size="small">
      <TableBody>
        {text.map((row) => (
          <TableRow key={row[0]}>
            <TableCell key={row[0]}>{row[0]}</TableCell>
            <TableCell key={row[1]}>{row[1]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export interface TraditionalOperationDetails {
  advance: number;
  rate: number;
  operationAmount: number;
  operationAdvanced: number;
  operationCostRate: number;
  operationToDeposit: number;
  operationAccountReceivable: number;
  operationIva: number;
  expensesIva: number;
  operationCostComissionFixed: number;
  operationCostComissionVariable: number;
  expensesCost: number;
  documentsWithDetails: any[];
  arrearType: string;
  arrearTableId: string | null;
  fundRate: number;
}

const calculateRequestDetailsTraditional = (
  requestForCreation: any,
): TraditionalOperationDetails => {
  const formData = {
    advance: requestForCreation.advance,
    rate: requestForCreation.rate,
    operationCostComissionFixed:
      requestForCreation.operationCostComissionFixed ||
      requestForCreation.operationCostFixed ||
      0,
    operationCostComissionVariable:
      requestForCreation.operationCostComissionVariable ||
      requestForCreation.operationCostVariable ||
      0,
    expensesCost: requestForCreation.expensesCost || 0,
    arrearType: requestForCreation.arrearType || 'lineal',
    arrearTableId: requestForCreation.arrearTableId || null,
    exchangeDays: requestForCreation.exchangeDays || 0,
    expensesHasIva: requestForCreation.expensesHasIva || false,
    fundRate: requestForCreation.fundRate || 0,
  };

  let operationAmount = 0;
  let operationAdvanced = 0;
  let operationCostRate = 0;
  const documentsWithDetails = [];
  for (const document of requestForCreation.documents) {
    const documentAmount = document.amount * (document.active ? 1 : 0);
    const { daysToCount } = document;
    const amountFinanced = Math.round(
      documentAmount * (formData.advance / 100),
    );
    const amountRetention = Math.round(
      documentAmount * (1 - formData.advance / 100),
    );
    const amountDocument = documentAmount - amountRetention;
    const costRate = daysToCount
      ? Math.round(
          ((daysToCount + Number(formData.exchangeDays)) / 30) *
            (formData.rate / 100) *
            amountFinanced,
        )
      : 0;
    operationAmount += documentAmount;
    operationAdvanced += amountFinanced;
    operationCostRate += costRate;
    const amountToDeposit = amountFinanced;

    documentsWithDetails.push({
      ...document,
      amountDocument,
      amountFinanced,
      amountRetention,
      amountToDeposit,
      costRate,
    });
  }
  const operationIva = Math.round(
    0.19 *
      (formData.operationCostComissionFixed +
        Math.round(formData.operationCostComissionVariable)),
  );

  const expensesIva = formData.expensesHasIva
    ? Math.round(0.19 * formData.expensesCost)
    : 0;
  const operationToDeposit = operationAdvanced || 0;
  const operationAccountReceivable =
    (formData.operationCostComissionFixed || 0) +
    (formData.operationCostComissionVariable || 0) +
    (formData.expensesCost || 0) +
    (operationCostRate || 0) +
    (operationIva || 0) +
    (expensesIva || 0);
  return {
    advance: formData.advance,
    rate: formData.rate,
    operationAmount,
    operationAdvanced,
    operationCostRate,
    operationToDeposit,
    operationAccountReceivable,
    operationIva,
    expensesIva,
    operationCostComissionFixed: formData.operationCostComissionFixed,
    operationCostComissionVariable: formData.operationCostComissionVariable,
    expensesCost: formData.expensesCost,
    documentsWithDetails,
    arrearType: formData.arrearType,
    arrearTableId: formData.arrearTableId,
    fundRate: formData.fundRate,
  };
};

const SummaryModule = (props: any) => {
  const {
    selectedRequest,
    setOpen,
    handleEdit,
    parentSetUp,
    executivesPortfolio,
    executivesAll,
  } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [operationDetails, setOperationDetails] = useState<
    Partial<TraditionalOperationDetails>
  >({});
  const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const updateStatusTransitions: UpdateStatusTransitions = {
    Aceptada: [
      {
        key: 'IN_REVIEW',
        label: 'En revisión',
      },
    ],
    'En giro': [
      ...(selectedRequest.comiteeOperationsId
        ? [
            {
              key: 'ACCEPTED',
              label: 'Aceptada',
            },
          ]
        : [
            {
              key: 'IN_REVIEW',
              label: 'En revisión',
            },
          ]),
    ],
  };

  const express = useSelector(
    (state: { auth: AuthState }) => state.auth.user?.express,
  );

  // Find executive name by ID for display
  const getExecutiveName = (executiveId: string | number) => {
    if (!executiveId) return '';

    // Search in portfolio executives first
    const portfolioExecutive = executivesPortfolio?.find((exec: any) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (portfolioExecutive) {
      return portfolioExecutive.name;
    }

    // Search in all executives
    const allExecutive = executivesAll?.find((exec: any) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (allExecutive) {
      return allExecutive.name;
    }

    return '';
  };

  // Force re-render when executives are loaded
  useEffect(() => {
    // This effect will trigger a re-render when executives are loaded
  }, [executivesPortfolio, executivesAll]);

  const canEditRequest = () => {
    if (
      !updateStatusTransitions[selectedRequest.status] ||
      updateStatusTransitions[selectedRequest.status].length === 0
    )
      return false;
    if (
      !validatePermissions(user, 'financing_confirming_edit_operation_status')
    )
      return false;
    return true;
  };

  const handleExportSimulation = async () => {
    setOpenExportDialog(true);
  };

  const setUp = async () => {
    setOperationDetails(calculateRequestDetailsTraditional(selectedRequest));
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = selectedRequest?.documents?.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const canShowDocumentProfile =
    selectedRequest.status === 'Aceptada' ||
    selectedRequest.status === 'Activa' ||
    selectedRequest.status === 'Completada';

  useEffect(() => {
    if (selectedRequest) {
      void setUp();
    }
  }, [selectedRequest]);

  return (
    <div>
      <Grid container>
        <Grid item xs={7}>
          <div
            style={{
              paddingRight: '10px',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography fontWeight="bold" variant="h6">
              Resumen solicitud
            </Typography>
            <OperationResume
              stakeholderName={selectedRequest.stakeholderName}
              stakeholderIdentifier={selectedRequest.stakeholderIdentifier}
              documents={selectedRequest.documents}
              productType={selectedRequest.type}
              requestDate={selectedRequest.operationDate.split('T')[0]}
            />
            <div style={{ padding: '20px 0px' }}>
              <Grid
                container
                style={gridStyle}
                columnSpacing={3}
                rowSpacing={1}
              >
                <Grid item xs={2.9}>
                  <Typography fontWeight={600}>Ejecutivo</Typography>
                </Grid>
                <Grid item xs={2.9}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography fontWeight={600}>Montos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Anticipo',
                          'Porcentaje a financiar respecto al total del valor de la operación.',
                        ],
                      ])}
                    >
                      <Info
                        style={{
                          marginLeft: 6,
                          fontSize: 14,
                        }}
                      />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={6.2}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontWeight={600}>Tasas</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Interés',
                          'Interés aplicado a la operación. Se descuenta del monto a financiar como diferencia de precio.',
                        ],
                        [
                          'Interés mora',
                          'Interés aplicado por cada día de mora en incrementos diarios.',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={2.9}>
                  <Typography variant="body1">Seleccionar ejecutivo</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={
                        selectedRequest.userOwner?.id ||
                        selectedRequest.userOwnerId ||
                        selectedRequest.executive ||
                        selectedRequest.executiveId ||
                        ''
                      }
                      disabled={true}
                      displayEmpty
                      renderValue={(value) => {
                        if (value) {
                          const name = getExecutiveName(value);
                          return name || 'Seleccionar ejecutivo';
                        }
                        return 'Seleccionar ejecutivo';
                      }}
                    >
                      <ListSubheader sx={{ fontWeight: 'bold' }}>
                        Cliente en cartera
                      </ListSubheader>
                      {executivesPortfolio?.map((executive: any) => (
                        <MenuItem key={executive.id} value={executive.id}>
                          {executive.name}
                        </MenuItem>
                      ))}
                      <ListSubheader sx={{ fontWeight: 'bold' }}>
                        Todos
                      </ListSubheader>
                      {executivesAll?.map((executive: any) => (
                        <MenuItem key={executive.id} value={executive.id}>
                          {executive.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2.9}>
                  <Typography variant="body1">Anticipo (%) </Typography>
                  <TextField
                    value={selectedRequest.advance}
                    placeholder="100"
                    type="number"
                    fullWidth
                    disabled={true}
                    style={{ padding: '0px !important' }}
                  />
                </Grid>
                <Grid item xs={1.7}>
                  <Typography variant="body1">Interés (%)</Typography>
                  <TextField
                    value={selectedRequest.rate}
                    type="number"
                    fullWidth
                    placeholder="2.2"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={2.1}>
                  <Typography variant="body1">Fondo (%)</Typography>
                  <TextField
                    value={selectedRequest.fundRate}
                    type="number"
                    fullWidth
                    placeholder="0"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <Typography variant="body1">Interés mora (%)</Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      value={
                        selectedRequest.arrearType === 'table'
                          ? 'Tabla'
                          : selectedRequest.arrearRate
                      }
                      type={
                        selectedRequest.arrearType === 'table'
                          ? 'text'
                          : 'number'
                      }
                      fullWidth
                      disabled={true}
                      placeholder="2.2"
                    />
                  </div>
                </Grid>
                <Grid item xs={5.8}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Comisión operación
                    </Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Monto fijo a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                        [
                          'Variable',
                          'Porcentaje a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Grid container columnSpacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="body1">Fijo ($)</Typography>
                      <TextField
                        value={formatMoney(selectedRequest.operationCostFixed)}
                        type="text"
                        fullWidth
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">Variable (%)</Typography>
                      <TextField
                        value={selectedRequest.operationCostVariablePercentage}
                        type="number"
                        fullWidth
                        disabled={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={3.1}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Otros gastos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Valor de cualquier otro gasto en el que se incurra en la operación',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Fijo ($)</Typography>
                  <TextField
                    value={formatMoney(selectedRequest.expensesCost)}
                    type="text"
                    fullWidth
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={3.1}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Canje</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Dás de canje',
                          'Días a sumar para el cálculo de la diferencia de precio',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Días de canje</Typography>
                  <TextField
                    value={selectedRequest.exchangeDays}
                    type="number"
                    fullWidth
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
        </Grid>
        <Grid item xs={5}>
          {selectedRequest.type === 'traditional' && (
            <SimulationTraditional
              expanded={true}
              onAccordionChange={() => () => {}}
              requestForCreation={selectedRequest}
              operationDetails={operationDetails}
              onExportSimulation={() => void handleExportSimulation()}
            />
          )}
        </Grid>
      </Grid>
      <Typography fontWeight="bold" padding="0px 0px 6px" variant="h6">
        Detalle documentos
      </Typography>
      <EnhancedTable
        dataHeaders={[
          ...(canShowDocumentProfile
            ? [
                {
                  key: 'folio',
                  label: 'Folio',
                  handleAction: onOpenDocumentProfile,
                  icon: <Info fontSize="small" />,
                },
              ]
            : [
                {
                  key: 'folio',
                  label: 'Folio',
                },
              ]),
          ...(selectedRequest.status === 'Activa' ||
          selectedRequest.status === 'Completada'
            ? [
                {
                  key: 'type',
                  label: 'Tipo',
                },
                {
                  key: 'paidAt',
                  label: 'Pagado',
                  type: 'paid-lights',
                },
                {
                  key: 'creditNotesDate',
                  label: 'NC',
                  type: 'cancel-lights',
                  canNull: true,
                },
                {
                  key: 'cedded',
                  label: 'Cedida',
                  type: 'cession-lights',
                },
                {
                  key: 'cancelDate',
                  label: 'Reclamos',
                  type: 'cancel-lights',
                  canNull: true,
                },
                {
                  key: 'confirmDate',
                  label: 'Confirmada',
                  type: 'confirm-lights',
                  canNull: true,
                },
              ]
            : []),
          {
            key: 'subType',
            label: 'Código Doc.',
          },
          {
            key: 'businessIdentifier',
            label: 'Rut Proveedor',
          },
          {
            key: 'businessName',
            label: 'Proveedor',
            ...(!express ? { handleAction: handleShowBusinessDetails } : {}),
            ...(!express ? { icon: <Info fontSize="small" /> } : {}),
          },
          {
            key: 'issuedDate',
            label: 'Emisión',
            type: 'date',
          },
          {
            key: 'receiptDate',
            label: 'Recepción',
            type: 'eight-days-date',
          },
          {
            key: 'amount',
            label: 'Monto Doc.',
            type: 'money',
          },
          {
            key: 'costRate',
            label: 'Dif. precio',
            type: 'money',
          },
          {
            key: 'paymentDateShortString',
            label: 'Vencimiento',
          },
          {
            key: 'daysToCount',
            label: 'Días',
          },
        ]}
        data={selectedRequest.documents.map((document: any) => ({
          ...document,
          costRate: Math.round(
            (document.daysToCount / 30) *
              (selectedRequest.rate / 100) *
              document.amount,
          ),
        }))}
        rowsPerPageDefault={5}
      />
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 0px',
          gap: '10px',
        }}
      >
        <div>
          {canEditRequest() && (
            <Typography
              color="orange"
              variant="body1"
              fontWeight="bold"
              style={{ cursor: 'pointer' }}
              onClick={() => setOpenUpdateStatusModal(true)}
            >
              Editar Estado
            </Typography>
          )}
        </div>
        <div
          style={{
            marginTop: 10,
            paddingBottom: 10,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <CustomButton
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            Salir
          </CustomButton>
          {selectedRequest.status === 'En revisión' && (
            <CustomButton
              color="primary"
              onClick={() => {
                handleEdit(selectedRequest.id);
                setOpen(false);
              }}
              disabled={!validatePermissions(user, 'financing_confirming_edit')}
            >
              Editar
            </CustomButton>
          )}
        </div>
      </div>
      {openUpdateStatusModal && (
        <ConfirmingRequestUpdateStatusModal
          updateStatusTransitions={updateStatusTransitions}
          open={openUpdateStatusModal}
          setOpen={setOpenUpdateStatusModal}
          requestId={selectedRequest.id}
          setUp={parentSetUp}
          oldStatus={selectedRequest.status}
          selectedId={selectedRequest.id}
        />
      )}
      <ExportDialog
        open={openExportDialog}
        setOpen={setOpenExportDialog}
        selectedRequest={{
          ...selectedRequest,
          operationToDeposit: operationDetails.operationToDeposit,
        }}
      />
    </div>
  );
};

export default SummaryModule;
